import { Fetch } from '../helpers/fetchWrapper';

const uploadService = {
    _url: `${process.env.REACT_APP_HOUDINY_LEAD_API_URL}/uploads`,

    async presign({ fileName, fileSize, columnMapping, type }) {
        let res = await Fetch.post(`${this._url}/presign`, {
            fileName,
            fileSize,
            columnMapping,
            type,
        });
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const { message } = await res.json();
        throw new Error(message ?? 'Presign failed');
    },

    async uploadPartToS3(presignedUrl, blob) {
        const response = await fetch(presignedUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'Content-Type': 'application/octet-stream' },
        });
        if (!response.ok) {
            throw new Error(`S3 upload failed with status ${response.status}`);
        }
        const etag = response.headers.get('ETag');
        return etag;
    },

    async complete({ jobId, uploadId, s3Key, parts }) {
        let res = await Fetch.post(`${this._url}/complete`, {
            jobId,
            uploadId,
            s3Key,
            parts,
        });
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const { message } = await res.json();
        throw new Error(message ?? 'Complete failed');
    },

    async getJobStatus(jobId) {
        let res = await Fetch.get(`${this._url}/status/${jobId}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const { message } = await res.json();
        throw new Error(message ?? 'Status check failed');
    },

    async uploadLargeCSV({ file, columnMapping, fileType, onProgress, onStatusUpdate, onComplete, onError }) {
        let stopPolling = false;

        try {
            // Step 1 — Presign
            onProgress?.({ percent: 5, message: 'Requesting upload credentials…' });

            // Flip mapping from { csvCol: systemKey } to { systemKey: csvCol }
            const apiMapping = {};
            Object.entries(columnMapping).forEach(([csvCol, systemKey]) => {
                apiMapping[systemKey] = csvCol;
            });

            const presignRes = await this.presign({
                fileName: file.name,
                fileSize: file.size,
                columnMapping: apiMapping,
                type: fileType,
            });

            // API wraps response in { success, message, data: { ... } }
            const { jobId, uploadId, s3Key, partSize, presignedUrls } = presignRes.data || presignRes;

            // Step 2 — Upload parts to S3 using presigned URLs
            const totalParts = presignedUrls.length;
            const parts = [];

            for (let i = 0; i < totalParts; i++) {
                const start = i * partSize;
                const end = Math.min(start + partSize, file.size);
                const blob = file.slice(start, end);

                // presignedUrls is [{ partNumber, url }] — extract the url
                const presignedUrl = presignedUrls[i].url;
                const etag = await this.uploadPartToS3(presignedUrl, blob);
                parts.push({ ETag: etag, PartNumber: presignedUrls[i].partNumber });

                const uploadPercent = Math.round(((i + 1) / totalParts) * 80) + 10;
                onProgress?.({ percent: uploadPercent, message: `Uploading part ${i + 1} of ${totalParts}…` });
            }

            // Step 3 — Complete
            onProgress?.({ percent: 92, message: 'Finalizing upload…' });
            const completeRes = await this.complete({ jobId, uploadId, s3Key, parts });

            // Step 4 — Poll status
            onProgress?.({ percent: 95, message: 'Processing file on server…' });

            const poll = async () => {
                while (!stopPolling) {
                    try {
                        const statusRes = await this.getJobStatus(jobId);
                        const status = statusRes.data || statusRes;
                        onStatusUpdate?.({
                            status: status.status,
                            totalRows: status.totalRows || 0,
                            processedRows: status.processedRows || 0,
                        });

                        if (status.status === 'completed') {
                            onProgress?.({ percent: 100, message: 'Done!' });
                            onComplete?.(status);
                            return;
                        }

                        if (status.status === 'failed') {
                            throw new Error(status.error || 'Processing failed on server');
                        }
                    } catch (err) {
                        if (!stopPolling) onError?.(err);
                        return;
                    }

                    await new Promise(r => setTimeout(r, 3000));
                }
            };

            poll();

            return {
                jobId,
                stopPolling: () => {
                    stopPolling = true;
                },
            };
        } catch (err) {
            onError?.(err);
            throw err;
        }
    },
};

export default uploadService;